import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    AlertCircle,
    Edit3,
    Eye,
    EyeOff,
    FilePlus2,
    Loader2,
    Plus,
    RefreshCw,
    Save,
    Trash2,
    X,
} from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/blog`;
const ADMIN_LIST_API = `${BACKEND_URL}/api/blog/admin/list`;

const EMPTY_POST = {
    slug: "",
    title: "",
    excerpt: "",
    cover_image: "",
    cover_image_alt: "",
    category: "General",
    tags: "",
    body_markdown: "",
    faqs: [],
    author: "Reformity Team",
    published: true,
    reading_time_min: 5,
    related_services: "",
};

const CATEGORIES = [
    "General",
    "Fencing",
    "Interlocking",
    "Decking",
    "Concrete",
    "Landscaping",
    "Renovations",
];

const KNOWN_SERVICES = [
    "fencing",
    "interlocking",
    "decking",
    "concrete",
    "landscaping",
    "renovations",
];

const toCsv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
const fromCsv = (str) =>
    String(str || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

// Normalize an axios/FastAPI error into a single human-readable string.
// FastAPI returns 422 with `detail` as an array of error objects — rendering
// that directly in JSX would throw "Objects are not valid as a React child".
const extractErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
        return detail
            .map((d) => {
                const path = Array.isArray(d?.loc)
                    ? d.loc.filter((seg) => seg !== "body").join(".")
                    : "";
                const msg = d?.msg || "Invalid value";
                return path ? `${path}: ${msg}` : msg;
            })
            .join("; ");
    }
    if (detail && typeof detail === "object") {
        return detail.msg || JSON.stringify(detail);
    }
    return err?.message || fallback || "Something went wrong.";
};

// Normalize FAQ items coming from the server (defensive — accepts either
// {q, a} or legacy {question, answer} shapes; drops anything else).
const normalizeFaqs = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((f) => {
            if (!f || typeof f !== "object") return null;
            const q = String(f.q ?? f.question ?? "");
            const a = String(f.a ?? f.answer ?? "");
            return { q, a };
        })
        .filter(Boolean);
};

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-CA", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const slugify = (input = "") =>
    input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

const Field = ({ label, children, hint }) => (
    <label className="block mb-4">
        <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
            {label}
        </span>
        {children}
        {hint && <span className="block text-[11px] text-brand-gray mt-1">{hint}</span>}
    </label>
);

const TextInput = (props) => (
    <input
        {...props}
        className={cn(
            "block w-full bg-white border border-brand-dark/15 px-3 py-2 text-sm text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none transition-colors",
            props.className,
        )}
    />
);

const TextArea = (props) => (
    <textarea
        {...props}
        className={cn(
            "block w-full bg-white border border-brand-dark/15 px-3 py-2 text-sm text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none transition-colors font-mono",
            props.className,
        )}
    />
);

const SelectInput = (props) => (
    <select
        {...props}
        className={cn(
            "block w-full bg-white border border-brand-dark/15 px-3 py-2 text-sm text-brand-dark focus:border-brand-cyan focus:outline-none transition-colors",
            props.className,
        )}
    >
        {props.children}
    </select>
);

const FAQEditor = ({ faqs, onChange }) => {
    const setFaq = (i, key, value) => {
        const next = faqs.map((f, idx) => (idx === i ? { ...f, [key]: value } : f));
        onChange(next);
    };
    const addFaq = () => onChange([...faqs, { q: "", a: "" }]);
    const removeFaq = (i) => onChange(faqs.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-3">
            {faqs.length === 0 && (
                <p className="text-xs text-brand-gray italic">No FAQs yet — add one below.</p>
            )}
            {faqs.map((f, i) => (
                <div
                    key={i}
                    className="bg-brand-offwhite border border-brand-dark/10 p-4 space-y-2"
                    data-testid={`admin-blog-faq-${i}`}
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-brand-gray">
                            FAQ {i + 1}
                        </span>
                        <button
                            type="button"
                            onClick={() => removeFaq(i)}
                            className="text-brand-coral hover:text-brand-dark transition-colors"
                            aria-label={`Remove FAQ ${i + 1}`}
                            data-testid={`admin-blog-faq-remove-${i}`}
                        >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </div>
                    <TextInput
                        value={f.q}
                        onChange={(e) => setFaq(i, "q", e.target.value)}
                        placeholder="Question"
                        data-testid={`admin-blog-faq-q-${i}`}
                    />
                    <TextArea
                        value={f.a}
                        rows={3}
                        onChange={(e) => setFaq(i, "a", e.target.value)}
                        placeholder="Answer"
                        data-testid={`admin-blog-faq-a-${i}`}
                    />
                </div>
            ))}
            <button
                type="button"
                onClick={addFaq}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-brand-cyan hover:text-brand-dark transition-colors"
                data-testid="admin-blog-faq-add"
            >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
                Add FAQ
            </button>
        </div>
    );
};

const PostEditor = ({ initial, onSave, onCancel, saving, error }) => {
    const [form, setForm] = useState(() => ({
        ...EMPTY_POST,
        ...initial,
        tags: toCsv(initial?.tags),
        related_services: toCsv(initial?.related_services),
        faqs: normalizeFaqs(initial?.faqs),
    }));

    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setForm((prev) => {
            const shouldAutoSlug = !initial?.slug && (!prev.slug || prev.slug === slugify(prev.title));
            return {
                ...prev,
                title,
                slug: shouldAutoSlug ? slugify(title) : prev.slug,
            };
        });
    };

    const setFaqs = (faqs) => setForm((prev) => ({ ...prev, faqs }));

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            slug: slugify(form.slug || form.title),
            title: form.title.trim(),
            excerpt: form.excerpt.trim(),
            cover_image: form.cover_image.trim(),
            cover_image_alt: form.cover_image_alt.trim(),
            category: form.category.trim() || "General",
            tags: fromCsv(form.tags),
            body_markdown: form.body_markdown,
            faqs: form.faqs.filter((f) => (f.q || "").trim() && (f.a || "").trim()),
            author: form.author.trim() || "Reformity Team",
            published: !!form.published,
            reading_time_min: Number(form.reading_time_min) || 5,
            related_services: fromCsv(form.related_services),
        };
        onSave(payload);
    };

    return (
        <form
            onSubmit={submit}
            className="bg-white border border-brand-dark/10 p-6 md:p-8 mb-6"
            data-testid="admin-blog-editor"
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
                <Field label="Title">
                    <TextInput
                        value={form.title}
                        onChange={handleTitleChange}
                        required
                        data-testid="admin-blog-field-title"
                    />
                </Field>
                <Field label="Slug" hint="URL fragment, e.g. cedar-vs-vinyl-fence">
                    <TextInput
                        value={form.slug}
                        onChange={(e) => setForm((p) => ({ ...p, slug: slugify(e.target.value) }))}
                        required
                        data-testid="admin-blog-field-slug"
                    />
                </Field>

                <Field label="Excerpt (≤ 500 chars)">
                    <TextArea
                        value={form.excerpt}
                        rows={3}
                        onChange={set("excerpt")}
                        required
                        maxLength={500}
                        data-testid="admin-blog-field-excerpt"
                    />
                </Field>
                <Field label="Category">
                    <SelectInput
                        value={form.category}
                        onChange={set("category")}
                        data-testid="admin-blog-field-category"
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </SelectInput>
                </Field>

                <Field label="Cover image path" hint="e.g. /projects/fencing/cedar-fence-img132-20f12.webp">
                    <TextInput
                        value={form.cover_image}
                        onChange={set("cover_image")}
                        data-testid="admin-blog-field-cover"
                    />
                </Field>
                <Field label="Cover image alt text">
                    <TextInput
                        value={form.cover_image_alt}
                        onChange={set("cover_image_alt")}
                        data-testid="admin-blog-field-cover-alt"
                    />
                </Field>

                <Field label="Tags (comma-separated)">
                    <TextInput
                        value={form.tags}
                        onChange={set("tags")}
                        placeholder="fencing, cedar, gta"
                        data-testid="admin-blog-field-tags"
                    />
                </Field>
                <Field
                    label="Related services (comma-separated slugs)"
                    hint={`Valid: ${KNOWN_SERVICES.join(", ")}`}
                >
                    <TextInput
                        value={form.related_services}
                        onChange={set("related_services")}
                        placeholder="fencing, landscaping"
                        data-testid="admin-blog-field-related"
                    />
                </Field>

                <Field label="Author">
                    <TextInput
                        value={form.author}
                        onChange={set("author")}
                        data-testid="admin-blog-field-author"
                    />
                </Field>
                <Field label="Reading time (min)">
                    <TextInput
                        type="number"
                        min="1"
                        max="60"
                        value={form.reading_time_min}
                        onChange={set("reading_time_min")}
                        data-testid="admin-blog-field-reading"
                    />
                </Field>
            </div>

            <div className="mb-4">
                <Field label="Body (Markdown)">
                    <TextArea
                        value={form.body_markdown}
                        rows={18}
                        onChange={set("body_markdown")}
                        placeholder="Write the article body in Markdown. ## Headings, **bold**, [links](/services/fencing), tables, lists all work."
                        data-testid="admin-blog-field-body"
                    />
                </Field>
            </div>

            <div className="mb-6">
                <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-2">
                    FAQs
                </span>
                <FAQEditor faqs={form.faqs} onChange={setFaqs} />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4 pt-5 border-t border-brand-dark/10">
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="admin-blog-published-toggle">
                    <input
                        type="checkbox"
                        checked={!!form.published}
                        onChange={set("published")}
                        className="h-4 w-4 accent-brand-cyan"
                    />
                    <span className="text-sm font-semibold text-brand-dark">
                        Published (visible on /blog)
                    </span>
                </label>

                <div className="flex items-center gap-3">
                    <BrandButton
                        type="button"
                        variant="outline"
                        tone="onLight"
                        size="md"
                        onClick={onCancel}
                        disabled={saving}
                        data-testid="admin-blog-cancel"
                    >
                        Cancel
                    </BrandButton>
                    <BrandButton
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={saving}
                        data-testid="admin-blog-save"
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                        ) : (
                            <Save className="h-4 w-4" strokeWidth={2.25} />
                        )}
                        Save Post
                    </BrandButton>
                </div>
            </div>

            {error && (
                <p
                    className="mt-3 flex items-start gap-1.5 text-xs text-brand-coral"
                    data-testid="admin-blog-editor-error"
                >
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5" strokeWidth={2.5} />
                    {error}
                </p>
            )}
        </form>
    );
};

const AdminBlogPanel = ({ token }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [editorMode, setEditorMode] = useState("idle"); // 'idle' | 'create' | 'edit'
    const [editorInitial, setEditorInitial] = useState(null);
    const [editorError, setEditorError] = useState("");
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all"); // all | published | draft

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

    const draftCount = useMemo(() => posts.filter((p) => !p.published).length, [posts]);
    const publishedCount = posts.length - draftCount;
    const visiblePosts = useMemo(() => {
        if (statusFilter === "published") return posts.filter((p) => p.published);
        if (statusFilter === "draft") return posts.filter((p) => !p.published);
        return posts;
    }, [posts, statusFilter]);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(ADMIN_LIST_API, { headers });
            setPosts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Could not load posts. Check the token and try refreshing.",
            );
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const handleCreate = () => {
        setEditorInitial(EMPTY_POST);
        setEditorMode("create");
        setEditorError("");
    };

    const handleEdit = (post) => {
        setEditorInitial(post);
        setEditorMode("edit");
        setEditorError("");
    };

    const handleCancel = () => {
        setEditorInitial(null);
        setEditorMode("idle");
        setEditorError("");
    };

    const handleSave = async (payload) => {
        setSaving(true);
        setEditorError("");
        try {
            if (editorMode === "create") {
                await axios.post(PUBLIC_API, payload, { headers });
            } else if (editorMode === "edit" && editorInitial?.id) {
                await axios.put(`${PUBLIC_API}/${editorInitial.id}`, payload, {
                    headers,
                });
            }
            await fetchPosts();
            handleCancel();
        } catch (err) {
            setEditorError(
                extractErrorMessage(
                    err,
                    "Could not save the post. Check the slug isn't already in use.",
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (post) => {
        if (!post?.id) return;
        const ok = window.confirm(`Delete "${post.title}"? This cannot be undone.`);
        if (!ok) return;
        setDeletingId(post.id);
        try {
            await axios.delete(`${PUBLIC_API}/${post.id}`, { headers });
            await fetchPosts();
        } catch (err) {
            setError(extractErrorMessage(err, "Delete failed. Try again."));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div data-testid="admin-blog-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none">
                        Blog Posts
                    </h2>
                    <p className="mt-2 text-sm text-brand-dark/70" data-testid="admin-blog-total">
                        <span className="font-semibold text-brand-dark">{posts.length}</span>{" "}
                        post{posts.length === 1 ? "" : "s"} — published &amp; drafts.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <BrandButton
                        variant="outline"
                        tone="onLight"
                        size="md"
                        onClick={fetchPosts}
                        disabled={loading}
                        data-testid="admin-blog-refresh"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                        ) : (
                            <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
                        )}
                        Refresh
                    </BrandButton>
                    {editorMode === "idle" && (
                        <BrandButton
                            variant="primary"
                            size="md"
                            onClick={handleCreate}
                            data-testid="admin-blog-new"
                        >
                            <FilePlus2 className="h-4 w-4" strokeWidth={2.25} />
                            New Post
                        </BrandButton>
                    )}
                </div>
            </div>

            {editorMode !== "idle" && (
                <PostEditor
                    initial={editorInitial}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    saving={saving}
                    error={editorError}
                />
            )}

            {error && (
                <div
                    className="mb-6 border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark"
                    data-testid="admin-blog-error"
                >
                    <p className="font-semibold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                        {error}
                    </p>
                </div>
            )}

            {/* Status filter — quickly surface Claude's pending drafts for review */}
            <div className="flex items-center gap-2 mb-4" role="tablist" aria-label="Filter posts by status">
                {[
                    { key: "all", label: "All", count: posts.length },
                    { key: "published", label: "Published", count: publishedCount },
                    { key: "draft", label: "Drafts", count: draftCount },
                ].map((f) => {
                    const active = statusFilter === f.key;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setStatusFilter(f.key)}
                            className={cn(
                                "inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border transition-colors",
                                active
                                    ? "bg-brand-dark text-white border-brand-dark"
                                    : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                            )}
                            data-testid={`admin-blog-status-filter-${f.key}`}
                        >
                            {f.label}
                            <span className={cn("text-[10px]", active ? "text-white/70" : "text-brand-gray")}>
                                ({f.count})
                            </span>
                        </button>
                    );
                })}
            </div>

            <div
                className="bg-white border border-brand-dark/10 overflow-x-auto"
                data-testid="admin-blog-table-wrapper"
            >
                <table className="w-full text-left">
                    <thead className="bg-brand-dark text-white text-[10px] uppercase tracking-wider font-semibold">
                        <tr>
                            <th className="px-3 py-3">Title</th>
                            <th className="px-3 py-3">Slug</th>
                            <th className="px-3 py-3">Category</th>
                            <th className="px-3 py-3">Status</th>
                            <th className="px-3 py-3">Updated</th>
                            <th className="px-3 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visiblePosts.length === 0 && !loading && (
                            <tr>
                                <td colSpan={6} className="px-3 py-10 text-center text-brand-gray text-sm">
                                    {posts.length === 0
                                        ? "No posts yet. Create your first post."
                                        : "No posts match this filter."}
                                </td>
                            </tr>
                        )}
                        {visiblePosts.map((p) => (
                            <tr
                                key={p.id}
                                className="border-b border-brand-dark/10 hover:bg-brand-offwhite transition-colors"
                                data-testid={`admin-blog-row-${p.slug}`}
                            >
                                <td className="px-3 py-3 text-sm font-semibold text-brand-dark max-w-xs">
                                    <div className="line-clamp-2">{p.title}</div>
                                </td>
                                <td className="px-3 py-3 text-xs font-mono text-brand-dark/70 whitespace-nowrap">
                                    /blog/{p.slug}
                                </td>
                                <td className="px-3 py-3 text-xs text-brand-dark/80 whitespace-nowrap">
                                    {p.category}
                                </td>
                                <td className="px-3 py-3 text-xs whitespace-nowrap">
                                    {p.published ? (
                                        <span className="inline-flex items-center gap-1 bg-brand-cyan/15 text-brand-cyan px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px]">
                                            <Eye className="h-3 w-3" strokeWidth={2.5} />
                                            Published
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 bg-brand-gray/20 text-brand-dark/70 px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px]">
                                            <EyeOff className="h-3 w-3" strokeWidth={2.5} />
                                            Draft
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-xs text-brand-dark/70 whitespace-nowrap">
                                    {formatDate(p.updated_at)}
                                </td>
                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                    <div className="inline-flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleEdit(p)}
                                            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-brand-cyan hover:text-brand-dark transition-colors"
                                            data-testid={`admin-blog-edit-${p.slug}`}
                                        >
                                            <Edit3 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(p)}
                                            disabled={deletingId === p.id}
                                            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-brand-coral hover:text-brand-dark transition-colors disabled:opacity-50"
                                            data-testid={`admin-blog-delete-${p.slug}`}
                                        >
                                            {deletingId === p.id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                                            ) : (
                                                <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            )}
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminBlogPanel;
